const React = require('react');
const T = require('prop-types');
const Moment = require('moment');
const { default: Styled } = require('styled-components');
const KeyMirror = require('keymirror');
const Avatar = require('components/ProfileAvatar');
const { default: Menu } = require('@mui/material/Menu');
const { default: MenuItem } = require('@mui/material/MenuItem');
const { default: MuiCircularProgress } = require('@mui/material/CircularProgress');
const IconMenuButton = require('components/IconMenuButton');
const Copy = require('clipboard-copy');
const { default: FaceIcon } = require('@mui/icons-material/Face');
const { default: MoreIcon } = require('@mui/icons-material/MoreVert');
const { default: TextField } = require('@mui/material/TextField');
const { default: FlatButton } = require('@mui/material/Button');
const { NavLink: Link } = require('react-router-dom');
const ItemBody = require('./ItemBody');
const ChatUtils = require('./utils');
const { getSizedImageUrl } = require('utils/image');
const { default: Classes } = require('./styles.scss');
const NoUserProfilePic = require('components/NoUserProfilePic');
const FixMuiTextareaAutosizeAriaLabel = require('utils/fixMui4TextareaAutosizeAriaLabel');
const FocusIndicator = require('components/FocusIndicator');
const ReactionButton = require('./ReactionButton');

const internals = {};
const modes = KeyMirror({
    VIEW: true,
    EDIT: true
});

const CHAT_AVATAR_SIZE = 44;

internals.doubleTapWindowMs = 325;

internals.messageHasImageOrGif = (messageBody) => {

    try {
        const messageData = typeof messageBody === 'string' ? JSON.parse(messageBody) : messageBody;
        if (messageData && typeof messageData === 'object' && (messageData.type === 'gif' || messageData.type === 'image')) {
            return true;
        }
    }
    catch (e) {
    }

    return false;
};

internals.isInteractiveTarget = (target) => {

    if (!target || typeof target.closest !== 'function') {
        return false;
    }

    return !!target.closest('button, a, input, textarea, [role="button"], [role="link"]');
};

module.exports = class ChatItem extends React.Component {

    static propTypes = {
        userId: T.number,
        classId: T.number,
        className: T.string,
        onSave: T.func.isRequired,
        onModerate: T.func.isRequired,
        onPin: T.func.isRequired,
        onRemove: T.func.isRequired,
        onRemoveOwn: T.func.isRequired,
        onFlagInappropriate: T.func.isRequired,
        onSendPrivateMessage: T.func,
        onSendConnectionRequest: T.func,
        onAcceptPeer: T.func,
        openSimpleDialogWithProps: T.func,
        onAddReaction: T.func.isRequired,
        onRemoveReaction: T.func.isRequired,
        updateAlertFunction: T.func,
        showNotification: T.func,
        rolesInteractions: T.arrayOf(T.shape({
            id: T.number,
            name: T.string,
            label: T.string,
            schoolId: T.number,
            canViewProfile: T.bool,
            canViewInGroup: T.bool,
            canChat: T.bool,
            canChatWithoutConnection: T.bool,
            canSeeConnections: T.bool,
            canSeeUsersGroups: T.bool,
            canSeeSurveyFields: T.bool,
            canSeeExtendedProfile: T.bool
        })),
        rolePermissions: T.shape({
            id: T.number,
            canPostInAnnouncement: T.bool,
            canPinMessage: T.bool,
            canDeleteMessages: T.bool
        }),
        showModerationControls: T.bool,
        message: T.shape({
            sid: T.string,
            id: T.string,
            channelSid: T.string,
            classId: T.number,
            conversationId: T.string,
            body: T.string.isRequired,
            timestamp: T.instanceOf(Date).isRequired,
            user: T.shape({
                id: T.oneOfType([T.string, T.number]).isRequired,
                firstName: T.string.isRequired,
                lastName: T.string.isRequired,
                croppedPicture: T.string.isRequired,
                isMe: T.bool,
                roleId: T.number,
                enabled: T.bool,
                peerStatus: T.string
            }),
            moderationStatus: T.oneOf(['moderated', 'ok']),
            isOptimistic: T.bool,
            isPinned: T.oneOf([false, true]),
            isEdited: T.oneOf([false, true]),
            reactions: T.object
        }),
        isAnnouncement: T.bool,
        isGroupChat: T.bool,
        hideMenu: T.bool,
        usersNameMap: T.object,
        isLatestMessage: T.bool,
        enableReactions: T.bool,
        listIndex: T.number,
        debug: T.bool,
        onOpenMediaViewer: T.func
    }

    static minHeight = 83; // A one-line chat message is this tall

    constructor(props) {

        super(props);

        this.state = {
            mode: modes.VIEW,
            editBody: null,
            menuOpen: false,
            anchorEl: null,
            showReactionButton: false,
            reactions: this.props.message.reactions || {}
        };

        this.input = null; // ref
        this.lastTapAt = 0;

        this.beginEditMode = this._beginEditMode.bind(this);
        this.endEditMode = this._endEditMode.bind(this);
        this.onBodyChange = this._onBodyChange.bind(this);
        this.save = this._save.bind(this);
        this.deleteOwn = this._deleteOwn.bind(this);
        this.delete = this._delete.bind(this);
        this.flagInappropriate = this._flagInappropriate.bind(this);
        this.sendPrivateMessage = this._sendPrivateMessage.bind(this);
        this.sendConnectionRequest = this._sendConnectionRequest.bind(this);
        this.moderate = this._moderate.bind(this);
        this.unmoderate = this._unmoderate.bind(this);
        this.pin = this._pin.bind(this);
        this.unpin = this._unpin.bind(this);
        this.setInputRef = this._setInputRef.bind(this);
        this.onMenuRequestChange = this._onMenuRequestChange.bind(this);
        this.handleMenuOpen = this._handleMenuOpen.bind(this);
        this.handleMenuClose = this._handleMenuClose.bind(this);
        this.copyText = this._copyText.bind(this);
        this.handleMouseEnter = this._handleMouseEnter.bind(this);
        this.handleMouseLeave = this._handleMouseLeave.bind(this);
        this.handleDoubleClick = this._handleDoubleClick.bind(this);
        this.handleTouchEnd = this._handleTouchEnd.bind(this);
        this.handleAddReaction = this._handleAddReaction.bind(this);
        this.handleRemoveReaction = this._handleRemoveReaction.bind(this);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {

        if (this.input) {
            FixMuiTextareaAutosizeAriaLabel(this.input, 'edit message element');
        }

        if (prevProps.message.id !== this.props.message.id || prevProps.message.sid !== this.props.message.sid || prevProps.message.reactions !== this.props.message.reactions) {
            this.setState({ reactions: this.props.message.reactions || {} });
        }
    }

    _onMenuRequestChange(open, reason) {

        if (open) {
            this.setState({ menuOpen: true });
        }
        else {
            this.setState({ menuOpen: false });
        }
    }

    _handleMenuOpen(event) {

        this.setState({ menuOpen: true, anchorEl: event.currentTarget });
    }

    _handleMenuClose() {

        this.setState({ menuOpen: false, anchorEl: null });
    }

    _beginEditMode() {

        this.setState({
            mode: modes.EDIT,
            editBody: ChatUtils.fullToEditBody(this.props.message.body)
        }, () => {

            setTimeout(() => {

                this.input.focus();
                this.handleMenuClose();
            }, 2); // Longer than clickCloseDelay
        });
    }

    _endEditMode() {

        this.setState({
            mode: modes.VIEW,
            editBody: null
        });
    }

    _onBodyChange(e) {

        this.setState({
            editBody: e.target.value
        });
    }

    _save() {

        // todo check edit mode

        const {
            classId,
            onSave,
            isAnnouncement,
            message: {
                id: messageId,
                conversationId,
                sid
            }
        } = this.props;

        if (isAnnouncement) {
            onSave({
                messageId,
                conversationId,
                classId,
                editBody: this.state.editBody
            });
        }
        else {
            onSave({
                sid,
                editBody: this.state.editBody
            });
        }

        this.endEditMode();
    }

    async _copyText() {

        const { message } = this.props;

        if (message && message.body) {
            const processMessage = ChatUtils.processFullBody({
                mention: ([name, id]) => {

                    return `@${name}`;
                }
            });

            const processedMessage = processMessage(message.body, true);
            const messageBody = [].concat(processedMessage).filter(Boolean).join('');

            await Copy(messageBody);

            this.props.showNotification('Message copied');
            this.handleMenuClose();
        }
    }

    _delete() {

        const { sid: messageSid, channelSid } = this.props.message;

        this.props.updateAlertFunction(() => {

            FocusIndicator.onBlurHandler();
            this.props.onRemove({ channelSid, messageSid });

        }, 'Are you sure you\'d like to delete this message?', 'Delete Message');

        this.handleMenuClose();
    }

    _deleteOwn() {

        const {
            isAnnouncement,
            onRemoveOwn,
            message: {
                id: messageId,
                sid: messageSid,
                channelSid,
                conversationId,
                classId
            }
        } = this.props;

        if (isAnnouncement) {
            this.props.updateAlertFunction(() => {

                FocusIndicator.onBlurHandler();
                onRemoveOwn({
                    conversationId: conversationId.toString(),
                    messageId: messageId.toString(),
                    classId: classId.toString()
                });

            }, 'Are you sure you\'d like to delete this message?', 'Delete Message');
        }
        else {
            this.props.updateAlertFunction(() => {

                this.props.onRemoveOwn({ channelSid, messageSid });

            }, 'Are you sure you\'d like to delete this message?', 'Delete Message');
        }

        this.handleMenuClose();
    }

    _flagInappropriate() {

        if (this.props.isAnnouncement) {
            const { id: messageId, classId } = this.props.message;

            this.props.updateAlertFunction(() => {

                this.props.onFlagInappropriate({ id: messageId, classId });
            }, 'Are you sure you\'d like to report this message as inappropriate?', 'Report inappropriate message');
        }
        else {
            this.props.updateAlertFunction(() => {

                this.props.onFlagInappropriate({ sid: this.props.message.sid });
            }, 'Are you sure you\'d like to report this message as inappropriate?', 'Report inappropriate message');
        }

        this.handleMenuClose();
    }

    _sendPrivateMessage() {

        const { user } = this.props.message;

        this.props.onSendPrivateMessage(user.id);
        this.handleMenuClose();
    }

    _sendConnectionRequest() {

        const { user } = this.props.message;
        const status = user.peerStatus;

        if (status === 'pending') {
            this.props.onAcceptPeer({ userId: user.id, firstName: user.firstName });
        }
        else if (status === 'waiting' || status === 'declinedByThem') {
            this.props.openSimpleDialogWithProps({
                title: 'Connection Request',
                message: 'You already sent a connection request',
                confirmLabel: 'OK',
                hideDecline: true
            });
        }
        else {
            this.props.onSendConnectionRequest({ userId: user.id });
        }

        this.handleMenuClose();
    }

    _moderate() {

        if (this.props.isAnnouncement) {
            const { id: messageId, conversationId, classId } = this.props.message;

            this.props.updateAlertFunction(() => {

                this.props.onModerate({ status: 'moderated', classId, conversationId: conversationId.toString(), messageId: messageId.toString() });
            }, 'Are you sure you\'d like to moderate this message?', 'Moderate Message');
        }
        else {
            const { sid: messageSid, channelSid } = this.props.message;

            this.props.updateAlertFunction(() => {

                this.props.onModerate({ status: 'moderated', channelSid, messageSid });

            }, 'Are you sure you\'d like to moderate this message?', 'Moderate Message');
        }

        this.handleMenuClose();
    }

    _pin() {

        if (this.props.isAnnouncement) {
            const { id: messageId, conversationId, classId } = this.props.message;

            this.props.updateAlertFunction(() => {

                this.props.onPin({
                    isPinned: true,
                    classId,
                    conversationId: conversationId.toString(),
                    messageId: messageId.toString()
                });
            }, 'Are you sure you\'d like to pin this message?', 'Pin Message');
        }
        else {
            const { sid: messageSid, channelSid } = this.props.message;

            this.props.updateAlertFunction(() => {

                this.props.onPin({ isPinned: true, channelSid, messageSid });
            }, 'Are you sure you\'d like to pin this message?', 'Pin Message');
        }

        this.handleMenuClose();
    }

    _unmoderate() {

        if (this.props.isAnnouncement) {
            const { id: messageId, conversationId, classId } = this.props.message;

            this.props.updateAlertFunction(() => {

                this.props.onModerate({
                    status: 'ok',
                    classId,
                    conversationId: conversationId.toString(),
                    messageId: messageId.toString()
                });
            }, 'Are you sure you\'d like to unmoderate this message?', 'Unmoderate Message');
        }
        else {
            const { sid: messageSid, channelSid } = this.props.message;

            this.props.updateAlertFunction(() => {

                this.props.onModerate({ status: 'ok', channelSid, messageSid });

            }, 'Are you sure you\'d like to unmoderate this message?', 'Unmoderate Message');
        }

        this.handleMenuClose();
    }

    _unpin() {

        const {
            isAnnouncement,
            onPin,
            updateAlertFunction,
            message: {
                id: messageId,
                conversationId,
                classId
            }
        } = this.props;

        if (isAnnouncement) {
            updateAlertFunction(() => {

                onPin({
                    isPinned: false,
                    classId,
                    conversationId: conversationId.toString(),
                    messageId: messageId.toString()
                });
            }, 'Are you sure you\'d like to unpin this message?', 'Unpin Message');
        }
        else {
            const { sid: messageSid, channelSid } = this.props.message;

            updateAlertFunction(() => {

                onPin({
                    isPinned: false,
                    channelSid,
                    messageSid
                });

            }, 'Are you sure you\'d like to unpin this message?', 'Unpin Message');
        }

        this.handleMenuClose();
    }

    _setInputRef(input) {

        this.input = input;
    }

    _handleMouseEnter() {

        this.setState({ showReactionButton: true });
    }

    _handleMouseLeave() {

        this.setState({ showReactionButton: false });
    }

    _handleDoubleClick() {

        this._addHeartReaction();
    }

    _handleTouchEnd(event) {

        if (internals.isInteractiveTarget(event.target)) {
            this.lastTapAt = 0;
            return;
        }

        const now = Date.now();
        const isDoubleTap = now - this.lastTapAt <= internals.doubleTapWindowMs;

        this.lastTapAt = now;

        if (!isDoubleTap) {
            return;
        }

        this.lastTapAt = 0;
        this._addHeartReaction();
    }

    _addHeartReaction() {

        if (!this.props.enableReactions) {
            return;
        }

        if (this.props.message.moderationStatus && this.props.message.moderationStatus !== 'ok') {
            return;
        }

        this.handleAddReaction({
            messageSid: this.props.message.id || this.props.message.sid,
            channelSid: this.props.message.channelSid,
            reaction: ':heart:'
        });
    }

    _handleAddReaction({ messageSid, channelSid, reaction }) {

        const {
            classId,
            userId
        } = this.props;

        const currentReactions = { ...this.state.reactions };

        Object.keys(currentReactions).forEach((existingReaction) => {

            const users = currentReactions[existingReaction];
            const userIndex = users.indexOf(userId);
            if (userIndex > -1) {
                if (users.length === 1) {
                    delete currentReactions[existingReaction];
                }
                else {
                    currentReactions[existingReaction] = [
                        ...users.slice(0, userIndex),
                        ...users.slice(userIndex + 1)
                    ];
                }
            }
        });

        const usersForReaction = currentReactions[reaction] || [];
        if (!usersForReaction.includes(userId)) {
            currentReactions[reaction] = [...usersForReaction, userId];
        }

        this.setState({ reactions: currentReactions });
        this.props.onAddReaction({ messageSid, channelSid, reaction, userId, classId });
    }

    _handleRemoveReaction({ messageSid, channelSid, reaction }) {

        const { userId } = this.props;
        const currentReactions = { ...this.state.reactions };
        const usersForReaction = currentReactions[reaction] || [];
        const userIndex = usersForReaction.indexOf(userId);

        if (userIndex > -1) {
            if (usersForReaction.length === 1) {
                delete currentReactions[reaction];
            }
            else {
                currentReactions[reaction] = [
                    ...usersForReaction.slice(0, userIndex),
                    ...usersForReaction.slice(userIndex + 1)
                ];
            }

            this.setState({ reactions: currentReactions });
        }

        this.props.onRemoveReaction({ messageSid, channelSid, reaction, userId });
    }

    render() {

        const {
            message,
            className,
            showModerationControls,
            rolesInteractions,
            rolePermissions,
            isAnnouncement,
            isGroupChat,
            hideMenu,
            userId,
            usersNameMap,
            enableReactions,
            listIndex,
            debug,
            ...rest
        } = this.props;

        const { styles, CircularProgress, SyncingIndicator, ChatItemContainer } = internals;

        const { menuOpen, showReactionButton } = this.state;
        const user = message.user;
        const isMe = !!(user && user.isMe);
        const isSending = message.isOptimistic && (!message.sendStatus || message.sendStatus === 'sending');
        const directionClass = isMe ? Classes.right : Classes.left;
        const isModerated = (message.moderationStatus === 'moderated');
        const showModeratedOriginal = isModerated && !!showModerationControls && !isMe;
        const moderatedNotice = isModerated ? ChatUtils.getModeratedMessageNotice({ isAuthor: isMe }) : null;
        const isPinned = (message.isPinned === true);
        const isEdited = (message.isEdited === true);
        const firstLastName = user ? user.firstName + ' ' + user.lastName : '';
        const messageContainsMedia = internals.messageHasImageOrGif(message.body);
        const hasReactions = !!(this.state.reactions && Object.keys(this.state.reactions).length > 0);
        const showReactionHint = !isMe && !isModerated && enableReactions && !hasReactions && this.props.isLatestMessage;

        let canViewUserProfile = false;

        if (user) {
            canViewUserProfile = rolesInteractions && rolesInteractions.find((roleInteraction) => {

                return roleInteraction.id === user.roleId;
            })?.canViewProfile;
        }

        return (
            <div>
                <ChatItemContainer
                    className={`${Classes.chatItemWrapper} ${directionClass} ${className}`}
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}
                    onDoubleClick={this.handleDoubleClick}
                    onTouchEnd={this.handleTouchEnd}
                    {...rest}
                >
                    <div className={`${Classes.talkBubble} ${hasReactions ? Classes.hasReactions : ''} ${showReactionHint ? Classes.hasReactionHint : ''}`}>
                        <div className={Classes.avatarWrapper}>
                        {(user && !isMe && (!isModerated || showModerationControls)) && (canViewUserProfile ? (
                            <Link
                                to={`/app/profile/${user.id}`}
                                className={`${Classes.avatar} ${Classes.avatarLink}`}
                                tabIndex={-1}
                                data-focus-outline={`radius:40${isPinned ? '' : ',zIndex:1'}`}
                            >
                                <Avatar
                                    className={Classes.avatarImg}
                                    src={getSizedImageUrl(user.croppedPicture, 100)}
                                    alt={firstLastName}
                                    size={CHAT_AVATAR_SIZE}
                                />
                            </Link>) :
                            <a
                                className={`${Classes.avatar} ${Classes.avatarLink}`}
                                onClick={() => {

                                    this.props.showNotification('Sorry, you cannot see this user\'s profile');
                                }}
                                tabIndex={-1}
                                data-focus-outline={`radius:40${isPinned ? '' : ',zIndex:1'}`}
                            >
                                <Avatar
                                    className={Classes.avatarImg}
                                    src={getSizedImageUrl(user.croppedPicture, 100)}
                                    alt={firstLastName}
                                    sizes={CHAT_AVATAR_SIZE}
                                />
                            </a>)}
                        {user ? ((isMe || (isModerated && !showModerationControls)) && (
                            <Link
                                to={user ? `/app/profile/${user.id}` : ''}
                                className={`${Classes.avatar} ${Classes.avatarLink}`}
                                tabIndex={-1}
                                data-focus-outline={`radius:40${isPinned ? '' : ',zIndex:1'}`}
                            >
                                {isMe && (
                                    <Avatar
                                        className={Classes.avatarImg}
                                        src={(user && isMe) && getSizedImageUrl(user.croppedPicture, 100)}
                                        alt={firstLastName}
                                        icon={(!user || !isMe) ? <FaceIcon /> : null}
                                        sizes={CHAT_AVATAR_SIZE}
                                    />
                                )}
                            </Link>
                        )) : <a
                            className={`${Classes.avatar} ${Classes.avatarLink} ${Classes.linkDisabled}`}
                            tabIndex={-1}
                            data-focus-outline={`radius:40${isPinned ? '' : ',zIndex:1'}`}
                        >
                            {<Avatar
                                className={Classes.avatarImg}
                                alt={firstLastName}
                                sizes={CHAT_AVATAR_SIZE}
                            >
                                {/* Leave this as this is for unknown users */}
                                <NoUserProfilePic iconColor={'#ffffff'} insideAvatar={true} outlined={false} />
                            </Avatar>}
                        </a>}
                        {user && !hideMenu && (!isAnnouncement || rolePermissions.canPostInAnnouncement) && <React.Fragment>
                            {isSending && (
                                <SyncingIndicator
                                    aria-label='Sending message'
                                    role='status'
                                    style={!isMe ? styles.syncingIndicator : styles.syncingIndicatorRight}
                                >
                                    <CircularProgress size={15} thickness={5} />
                                </SyncingIndicator>
                            )}
                            {!message.isOptimistic && (
                                <IconMenuButton
                                    aria-label='Message actions'
                                    aria-expanded={menuOpen}
                                    style={!isMe ? styles.menuIcon : styles.menuIconRight}
                                    onClick={this.handleMenuOpen}
                                    tabIndex={0}
                                    data-focus-outline={`radius:20,padding:2${isPinned ? '' : ',zIndex:1'}`}
                                >
                                    <MoreIcon />
                                </IconMenuButton>
                            )}
                            <Menu
                                className={className}
                                anchorEl={this.state.anchorEl}
                                open={menuOpen}
                                onClose={this.handleMenuClose}
                            >
                                {!messageContainsMedia && (!isModerated || showModeratedOriginal) && <MenuItem className={Classes.menuItem} onClick={this.copyText}><span >Copy message</span></MenuItem>}
                                {rolePermissions.canPinMessage && !isPinned &&
                                    <MenuItem className={Classes.menuItem} onClick={this.pin}><span >Pin message</span></MenuItem>
                                }
                                {rolePermissions.canPinMessage && isPinned &&
                                    <MenuItem className={Classes.menuItem} onClick={this.unpin}><span >Unpin message</span></MenuItem>
                                }
                                {isGroupChat && !isAnnouncement && user && !isMe && (
                                    user.peerStatus === 'accepted'
                                        ? <MenuItem className={Classes.menuItem} onClick={this.sendPrivateMessage}>Send Private Message</MenuItem>
                                        : <MenuItem className={Classes.menuItem} onClick={this.sendConnectionRequest}>Send Connection Request</MenuItem>
                                )}
                                {user && !isMe && <MenuItem className={Classes.menuItem} onClick={this.flagInappropriate}>Report inappropriate message</MenuItem>}
                                {!isMe && showModerationControls && !isModerated &&
                                    <MenuItem className={Classes.menuItem} onClick={this.moderate}><span style={styles.deleteText}>Place in moderation</span></MenuItem>
                                }
                                {!isMe && showModerationControls && isModerated &&
                                    <MenuItem className={Classes.menuItem} onClick={this.unmoderate}><span style={styles.deleteText}>Remove from moderation</span></MenuItem>
                                }
                                {isMe && !isModerated && !messageContainsMedia && <MenuItem className={Classes.menuItem} onClick={this.beginEditMode}>Edit</MenuItem>}
                                {isMe && !isModerated && <MenuItem className={Classes.menuItem} onClick={this.deleteOwn}><span style={styles.deleteText}>Delete</span></MenuItem>}
                                {showModerationControls && !isAnnouncement && isModerated && rolePermissions.canDeleteMessages && <MenuItem className={Classes.menuItem} onClick={this.delete}><span style={styles.deleteText}>Delete</span></MenuItem>}
                            </Menu>
                        </React.Fragment>}
                        </div>
                        <div className={Classes.chatItemInfo}>
                            <b className={`${Classes.name} ${isModerated ? Classes.isModerated : ' '}`}>
                                {(user && (isMe || !isModerated || showModerationControls)) ? `${user.firstName} ${user.lastName}` : '[Unknown User]'}
                                &nbsp;
                                <span className={`${(user && !user.enabled) ? Classes.userDisabled : ''}`} />
                            </b>
                            <div className={Classes.timestamp}>
                                {Moment(message.timestamp)
                                    .calendar(null, {
                                        lastDay: '[Yesterday] [at] LT',
                                        sameDay: '[Today] [at] LT',
                                        nextDay: '[Tomorrow]',
                                        lastWeek: 'dddd [at] LT',
                                        nextWeek: 'dddd',
                                        sameElse: 'MM/DD/YYYY [at] LT'
                                    })
                                }
                            </div>
                        </div>

                        <div className={Classes.talkText}>
                            {message.sendStatus === 'failed' && <div role='status'>Message not sent</div>}
                            {message.sendStatus === 'unconfirmed' && <div role='status'>Delivery not confirmed</div>}
                            {(this.state.mode === modes.VIEW) && <p>
                                {moderatedNotice && (
                                    <span className={Classes.isModerated} style={{ display: 'block', whiteSpace: 'pre-line' }}>
                                        {moderatedNotice}
                                    </span>
                                )}
                                {(!isModerated || showModeratedOriginal) && (
                                    <ItemBody
                                        container={<span style={{ whiteSpace: 'pre-line', userSelect: 'text' }} />}
                                        messageAttributes={message.attributes}
                                        onOpenMediaViewer={this.props.onOpenMediaViewer}
                                    >
                                        {message.body}
                                    </ItemBody>
                                )}
                            </p>}
                            {(this.state.mode === modes.EDIT) && <div>
                                <TextField
                                    id={`edit-message-${message.sid}`}
                                    ref={this.setInputRef}
                                    textareaStyle={styles.textarea}
                                    value={this.state.editBody}
                                    inputProps={{ 'aria-label': 'edit message input' }}
                                    multiline={true}
                                    fullWidth={true}
                                    maxRows={3}
                                    onChange={this.onBodyChange}
                                />
                                <div>
                                    <FlatButton
                                        color={'primary'}
                                        style={styles.saveButton}
                                        onClick={this.save}
                                    >
                                        Save
                                    </FlatButton>
                                    <FlatButton
                                        color={'secondary'}
                                        style={styles.cancelButton}
                                        onClick={this.endEditMode}
                                    >
                                        Cancel
                                    </FlatButton>
                                </div>
                            </div>}
                        </div>
                        {isEdited ? <div className={Classes.editStatus}>(edited)</div> : null}
                        {showReactionHint && (
                            <div className={Classes.addReactionText}>Double-tap to add reaction ❤️</div>
                        )}
                        {!isModerated && enableReactions && <ReactionButton
                            userId={userId}
                            messageId={message.id || message.sid}
                            channelSid={message.channelSid}
                            onAddReaction={this.handleAddReaction}
                            onRemoveReaction={this.handleRemoveReaction}
                            existingReactions={this.state.reactions}
                            className={Classes.reactionButton}
                            usersNameMap={usersNameMap}
                            forceShow={showReactionButton}
                        />}
                    </div>
                    {!!debug && (
                        <div style={{ position: 'absolute', top: 0, right: 0 }}>
                            {listIndex}
                        </div>
                    )}
                </ChatItemContainer>
            </div>
        );
    }
};

internals.initials = (firstName, lastName) => {

    const parts = (`${firstName} ${lastName}`).split(' ').filter((part) => part.length > 0);
    const letters = parts.map((part) => part[0].toUpperCase());

    return letters.join('');
};

internals.styles = {
    menuIcon: {
        padding: 0,
        height: 28,
        width: 28,
        right: 10,
        top: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.84)',
        border: 'none',
        boxShadow: 'none',
        color: '#667085',
        position: 'absolute',
        zIndex: 2
    },
    menuIconRight: {
        padding: 0,
        height: 28,
        width: 28,
        left: 10,
        top: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.84)',
        border: 'none',
        boxShadow: 'none',
        color: '#667085',
        position: 'absolute',
        zIndex: 2
    },
    syncingIndicator: {
        padding: 0,
        height: 28,
        width: 28,
        right: 10,
        top: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        border: '1px solid rgba(102, 112, 133, 0.18)',
        boxShadow: '0 1px 4px rgba(16, 24, 40, 0.14)',
        color: '#5846b8',
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: 2
    },
    syncingIndicatorRight: {
        padding: 0,
        height: 28,
        width: 28,
        left: 10,
        top: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        border: '1px solid rgba(102, 112, 133, 0.18)',
        boxShadow: '0 1px 4px rgba(16, 24, 40, 0.14)',
        color: '#5846b8',
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: 2
    },
    deleteText: {
        color: '#C00'
    },
    textarea: {
        background: 'white',
        border: '1px solid grey',
        lineHeight: '18px',
        fontSize: 16
    },
    saveButton: {
        zIndex: 0,
        height: 28,
        lineHeight: '28px',
        minWidth: 0,
        backgroundColor: 'white',
        marginRight: 4
    },
    cancelButton: {
        zIndex: 0,
        height: 28,
        lineHeight: '28px',
        minWidth: 0,
        backgroundColor: 'white'
    }
};

internals.SyncingIndicator = Styled.div`
    align-items: center;
    border-radius: 999px;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
`;

internals.CircularProgress = Styled(MuiCircularProgress)`
    && {
        color: currentColor;
    }
`;

internals.ChatItemContainer = Styled.div`
    &:after {
        content: "";
        display: block;
        clear: both;
    }

    min-height: 83px;
    position: relative;
`;
